import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { tagLabel, tagSlug } from "@/lib/blogTags";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { fetchPublishedPosts, type UnifiedPost } from "@/lib/blog-db";
import { absUrl, canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import heroImg from "@/assets/original/hero-shade.jpg";
import { ArrowRight, Calendar } from "lucide-react";

export const Route = createFileRoute("/blog/topic/$tag")({
  loader: async ({ params }) => {
    const tag = tagSlug(params.tag);
    const all = await fetchPublishedPosts();
    const posts = all.filter((p) => (p.tags ?? []).some((t) => tagSlug(t) === tag));
    // Never serve an empty topic page — send crawlers back to the blog index.
    if (posts.length === 0) throw redirect({ to: "/blog", statusCode: 301 });
    return { posts, tag };
  },
  head: (ctx) => {
    const label = tagLabel(tagSlug(ctx.params.tag));
    const title = `${label} Articles — Texas Made Shade & Covers Blog`;
    const description = `Articles about ${label.toLowerCase()} from the Texas Made Shade & Covers shop in Seguin, Texas — fabrics, welded frames, care tips and real Texas project stories.`;
    return {
      links: [canonicalLink(pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title,
        description,
        image: heroImg,
        socialDescription: description,
      }),
      scripts: pageHeadScripts({
        path: `/blog/topic/${tagSlug(ctx.params.tag)}`,
        name: title,
        description,
        parent: { name: "Blog", path: "/blog" },
      }),
    };
  },
  component: BlogTopic,
});

function BlogTopic() {
  const { posts, tag } = Route.useLoaderData() as {
    posts: UnifiedPost[];
    tag: string;
  };
  const label = tagLabel(tag);

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        <section className="bg-navy-deep py-16 text-white">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-xs tracking-[0.3em] text-rust">
              ★ FROM THE SHOP ★
            </div>
            <h1 className="mt-3 font-display text-4xl font-bold md:text-5xl">
              {label} Articles
            </h1>
            <p className="mt-4 max-w-2xl text-white/80">
              Everything we&rsquo;ve written about {label.toLowerCase()} — from our
              Seguin, Texas fabrication shop.
            </p>
            <Link
              to="/blog"
              className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-rust hover:text-white"
            >
              All blog posts <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <ul className="grid gap-8 md:grid-cols-2">
              {posts.map((p) => (
                <li
                  key={p.slug}
                  className="flex flex-col border-2 border-navy-deep/10 bg-white shadow-card"
                >
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="block aspect-[16/9] overflow-hidden"
                  >
                    {p.coverImage && (
                      <img
                        src={absUrl(p.coverImage)}
                        alt={p.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition hover:scale-105"
                      />
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-2 font-display text-[11px] uppercase tracking-[0.2em] text-navy-deep/60">
                      <Calendar className="h-3 w-3" aria-hidden="true" />
                      {new Date(p.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <h2 className="mt-2 font-display text-2xl font-bold text-navy-deep">
                      <Link
                        to="/blog/$slug"
                        params={{ slug: p.slug }}
                        className="hover:text-rust"
                      >
                        {p.title}
                      </Link>
                    </h2>
                    <p className="mt-3 flex-1 text-navy-deep/80">{p.excerpt}</p>
                    <Link
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="mt-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-rust hover:text-navy-deep"
                    >
                      Read post <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}
